import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { ProposalBuildPhase, ProposalBuildStage, ProposalBuildStageState, ProposalBuildState } from '@shared/api';

const STAGE_ORDER: ProposalBuildStage[] = ['strategy', 'assembly', 'imagery', 'quality'];
const STAGE_LABEL: Record<ProposalBuildStage, string> = {
  strategy: 'Strategy',
  assembly: 'Assembly',
  imagery: 'Imagery',
  quality: 'Quality',
};

const FALLBACK_ACTIVITY = [
  'Planning the next step…',
  'Preparing the next set of pages…',
  'Reviewing the current draft…',
];

/**
 * The slice of `ProposalBuildState` the tracker actually reads. Narrowed
 * (rather than requiring the full state) so a caller can feed it a synthetic
 * "starting" snapshot — all stages pending, one fixed activity line — for
 * the window between the rep's Build click and the server's own `phase`
 * flipping to `building` (docs: ProposalView.tsx G1/G2, web/src/lib/buildRail.ts).
 */
export interface StageTrackerState {
  phase: ProposalBuildPhase;
  mode?: 'live' | 'simulated';
  stages: ProposalBuildStageState[];
  activity: ProposalBuildState['activity'];
}

/** The fixed "waiting on the model's first turn" snapshot (G1). */
export function startingStageTrackerState(): StageTrackerState {
  return {
    phase: 'awaiting_confirmation',
    mode: 'live',
    stages: [],
    activity: { label: 'Starting the build', startedAt: new Date().toISOString() },
  };
}

/**
 * The one neutral activity line under the tracker. Never anything more
 * specific than `state.activity`; once that label has been sitting for more
 * than 5s (the server has gone quiet) it rotates through a short set of
 * generic phrases every 6s rather than looking stalled.
 */
function useActivityLabel(
  activity: ProposalBuildState['activity'],
  running: boolean,
  allowFallback: boolean,
): string | null {
  const [stale, setStale] = useState(false);
  const [fallbackIndex, setFallbackIndex] = useState(0);

  useEffect(() => {
    setStale(false);
    setFallbackIndex(0);
    // Simulated frames carry fixed timestamps, so the staleness fallback
    // would fire on every frame; only real runs can go quiet.
    if (!activity || !running || !allowFallback) return undefined;
    const startedAt = Date.parse(activity.startedAt);
    const elapsed = Number.isNaN(startedAt) ? 0 : Date.now() - startedAt;
    const delay = Math.max(0, 5000 - elapsed);
    const timer = window.setTimeout(() => setStale(true), delay);
    return () => window.clearTimeout(timer);
  }, [activity?.label, activity?.startedAt, running, allowFallback]);

  useEffect(() => {
    if (!stale || !running) return undefined;
    const interval = window.setInterval(
      () => setFallbackIndex((index) => (index + 1) % FALLBACK_ACTIVITY.length),
      6000,
    );
    return () => window.clearInterval(interval);
  }, [stale, running]);

  if (!activity) return null;
  return stale ? (FALLBACK_ACTIVITY[fallbackIndex] ?? FALLBACK_ACTIVITY[0] ?? 'Working…') : `${activity.label}…`;
}

function Segment({ stage }: { stage: ProposalBuildStageState }): ReactElement {
  const fill = stage.status === 'complete' ? 100 : stage.status === 'active' ? Math.round(stage.progress * 100) : 0;
  return (
    <div className={`stage-tracker-segment stage-tracker-segment-${stage.status}`}>
      <div className="stage-tracker-rail">
        <span className="stage-tracker-rail-fill" style={{ width: `${fill}%` }} />
      </div>
      <div className="stage-tracker-label-row">
        <span className="stage-tracker-dot" aria-hidden="true">
          {stage.status === 'complete' ? '✓' : '●'}
        </span>
        <span className="stage-tracker-label">{STAGE_LABEL[stage.id]}</span>
      </div>
    </div>
  );
}

/**
 * Four equal stage segments + one neutral activity line. The caller decides
 * when to mount/unmount it (see `isBuildInFlight` in `web/src/lib/buildRail.ts`
 * — no longer strictly `phase === 'building'`: G1/G2 extend visibility back
 * to the Build click and forward through the copy-landing window, using
 * `startingStageTrackerState()` for the pre-`building` snapshot). `variant`
 * is purely cosmetic: `'compact'` is a slimmer strip for rendering above an
 * already-populated grid (G2), rather than a whole idle build screen.
 */
export function StageTracker({
  state,
  variant = 'full',
}: {
  state: StageTrackerState;
  variant?: 'full' | 'compact';
}): ReactElement {
  const orderedStages = STAGE_ORDER.map(
    (id) => state.stages.find((stage) => stage.id === id) ?? { id, status: 'pending' as const, progress: 0 },
  );
  const activityLabel = useActivityLabel(state.activity, state.phase === 'building', state.mode !== 'simulated');

  return (
    <div className={`stage-tracker${variant === 'compact' ? ' stage-tracker-compact' : ''}`}>
      <div className="stage-tracker-segments">
        {orderedStages.map((stage) => (
          <Segment key={stage.id} stage={stage} />
        ))}
      </div>
      {activityLabel ? (
        <p className="stage-tracker-activity" aria-live="polite">
          {activityLabel}
        </p>
      ) : null}
    </div>
  );
}
